import { useState } from 'react';
import { api } from '../api';
import { styles } from '../styles';
import { BackIcon } from '../components/Icons';

export default function CategoryEditScreen({ category, parentCategory, onBack, onSaved, onAddSubCategory }) {
  const isNew = !category;
  const isSubCategory = !!parentCategory || !!category?.parent_id;
  const [name, setName] = useState(category?.name || '');
  const [budgetType, setBudgetType] = useState(category?.budget_type || 'monthly');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const generateSlug = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please enter a category name');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const slug = generateSlug(name);

      if (isNew) {
        const payload = { name, slug, sort_order: 50, budget_type: isSubCategory ? 'monthly' : budgetType };
        if (parentCategory) payload.parent_id = parentCategory.id;
        await api.post('/categories', payload);
      } else {
        await api.patch(`/categories/${category.id}`, { name, budget_type: budgetType });
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Deactivate "${category.name}"? Existing receipts will keep this category.`)) {
      return;
    }

    setDeleting(true);
    try {
      await api.delete(`/categories/${category.id}`);
      onSaved();
    } catch (e) {
      setError('Failed to delete');
      setDeleting(false);
    }
  };

  const headerTitle = isNew
    ? (parentCategory ? `New Sub-category` : 'New Category')
    : 'Edit Category';

  return (
    <div style={styles.screen}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>{headerTitle}</h1>
        <div style={{ width: 20 }} />
      </header>

      <div style={styles.form}>
        {parentCategory && (
          <div style={catEditStyles.parentBadge}>
            Sub-category of: {parentCategory.icon} {parentCategory.name}
          </div>
        )}

        <div style={styles.field}>
          <label style={styles.label}>Name</label>
          <input
            style={styles.input}
            type="text"
            placeholder={isSubCategory ? 'e.g. Food, Hotels, Kitchen' : 'e.g. Groceries, Entertainment'}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {!isSubCategory && (
          <div style={styles.field}>
            <label style={styles.label}>Budget Type</label>
            <div style={styles.segmentedControl}>
              <button
                style={{ ...styles.segment, ...(budgetType === 'monthly' ? styles.segmentActive : {}) }}
                onClick={() => setBudgetType('monthly')}
              >
                Monthly
              </button>
              <button
                style={{ ...styles.segment, ...(budgetType === 'project' ? styles.segmentActive : {}) }}
                onClick={() => setBudgetType('project')}
              >
                Project
              </button>
            </div>
            {budgetType === 'project' && (
              <p style={styles.hint}>Project categories track total spending with a one-time envelope budget (e.g. vacation, renovation).</p>
            )}
          </div>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <button
          style={styles.primaryBtn}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : (isNew ? 'Create Category' : 'Save Changes')}
        </button>

        {!isNew && budgetType === 'project' && onAddSubCategory && (
          <button
            style={catEditStyles.addSubBtn}
            onClick={() => onAddSubCategory(category)}
          >
            + Add Sub-category
          </button>
        )}

        {!isNew && (
          <button
            style={catEditStyles.deleteBtn}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Category'}
          </button>
        )}
      </div>
    </div>
  );
}

const catEditStyles = {
  deleteBtn: {
    padding: '14px 20px',
    fontSize: 15,
    fontWeight: 500,
    backgroundColor: 'transparent',
    color: '#ef4444',
    border: '1px solid #fecaca',
    borderRadius: 12,
    cursor: 'pointer',
    marginTop: 8,
    width: '100%',
  },
  addSubBtn: {
    padding: '14px 20px',
    fontSize: 15,
    fontWeight: 500,
    backgroundColor: 'transparent',
    color: '#6366f1',
    border: '1px solid #c7d2fe',
    borderRadius: 12,
    cursor: 'pointer',
    marginTop: 8,
    width: '100%',
  },
  parentBadge: {
    fontSize: 13,
    color: '#6366f1',
    backgroundColor: '#eef2ff',
    padding: '8px 12px',
    borderRadius: 8,
    marginBottom: 12,
  },
};
